import React, { useEffect, useRef, useCallback } from 'react';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Grow from '@mui/material/Grow';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { getTelegramWebApp } from '@/hooks/useTelegram';
import styles from './styles.module.scss';

interface PopupProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  popupClass?: string;
  /** Hide the default close button (when the content provides its own) */
  hideCloseButton?: boolean;
  /** Remove default card padding (when the content manages its own padding) */
  noPadding?: boolean;
}

export const Popup: React.FC<PopupProps> = ({ open, onClose, children, popupClass, hideCloseButton, noPadding }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Fit the popup into the visible viewport (shrinks above the virtual
  // keyboard). Focused fields are revealed by the browser natively and, inside
  // Telegram mobile, by `useTelegramKeyboardScrollFix` (the popup is their
  // nearest scroll container).
  useEffect(() => {
    if (!open) return undefined;

    // Refs are read lazily: Modal mounts its Portal content after this effect
    // first runs, so the nodes are not attached yet at that point.
    const tg = getTelegramWebApp();
    const viewport = window.visualViewport;
    let sizedContainer: HTMLDivElement | null = null;
    let sizedPopup: HTMLDivElement | null = null;

    // Visual viewport, not Telegram's viewportHeight: on iOS the latter keeps
    // the full height while the keyboard is up.
    const updatePopupLayout = () => {
      const container = containerRef.current;
      const popup = popupRef.current;
      if (!container || !popup) return;
      const viewportHeight = viewport?.height || window.innerHeight;
      // Don't adjust top — position:fixed top:0 is correct; using visualViewport.offsetTop
      // causes the container to shift when the page scrolls for keyboard focus,
      // leading to the popup "going up and getting stuck" in Telegram.
      container.style.height = `${viewportHeight}px`;
      popup.style.maxHeight = `${viewportHeight - 40}px`;
      sizedContainer = container;
      sizedPopup = popup;
    };

    // Start at the top of the popup when it opens.
    const scrollTimer = window.setTimeout(() => {
      updatePopupLayout();
      popupRef.current?.scrollTo({ top: 0, behavior: 'instant' });
      containerRef.current?.scrollTo({ top: 0, behavior: 'instant' });
    }, 100);

    updatePopupLayout();
    viewport?.addEventListener('resize', updatePopupLayout);
    window.addEventListener('resize', updatePopupLayout);
    tg?.onEvent('viewportChanged', updatePopupLayout);

    return () => {
      window.clearTimeout(scrollTimer);
      viewport?.removeEventListener('resize', updatePopupLayout);
      window.removeEventListener('resize', updatePopupLayout);
      tg?.offEvent('viewportChanged', updatePopupLayout);
      if (sizedContainer) sizedContainer.style.height = '';
      if (sizedPopup) sizedPopup.style.maxHeight = '';
    };
  }, [open]);

  // Prevent background scroll while allowing popup scroll
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    // Allow scrolling inside popup
    e.stopPropagation();
  }, []);

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open} timeout={500}>
        <div ref={containerRef} className={styles.container}>
          <ClickAwayListener onClickAway={onClose}>
            <Grow in={open} timeout={500}>
              <div
                ref={popupRef}
                className={`${styles.popup} card ${noPadding ? '' : 'card-padded'} ${popupClass || ''}`}
                onTouchMove={handleTouchMove}
              >
                {!hideCloseButton && (
                  <button
                    className={styles.closeBtn}
                    onClick={onClose}
                    type="button"
                    aria-label="Close popup"
                  >
                    <i className="icon-xmark" />
                  </button>
                )}
                {children}
              </div>
            </Grow>
          </ClickAwayListener>
        </div>
      </Fade>
    </Modal>
  );
};

export default Popup;
